import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { styled } from '@mui/material/styles'
import MuiFormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Box from '@mui/material/Box'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import MuiAlert from '@mui/material/Alert'
import MenuItem from '@mui/material/MenuItem'
import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import {
  getTestIdProp
} from '../../../../commonFunc'

const Root = styled(Box)({
  padding: '25px'
})

const FormControl = styled(MuiFormControl)({
  padding: '0 0 0 25px'
})

const OuterLabelContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'nowrap',
  width: '100%'
})

const InnerLabelContainer = styled(Box)({
  height: '40px',
  display: 'flex',
  alignItems: 'center'
})

const Alert = styled(MuiAlert)({
  fontSize: '.9rem',
  padding: '2px',
  alignItems: 'center',
  marginLeft: '16px',
  borderRadius: '8px',
  '& .MuiAlert-message': {
    flexGrow: 0,
    marginRight: '4px',
    padding: '0'
  },
  '& .MuiAlert-action': {
    margin: '0'
  },
  '& .MuiButtonBase-root': {
    margin: '0',
    padding: '0',
    minWidth: 'auto'
  }
})

Parameters.errorMessages = {
  observationDateRequired: 'Observation Date Required',
  periodStartDateRequired: 'Period Start Date Required',
  periodEndDateRequired: 'Period End Date Required'

}

Parameters.fieldNames = {
  observationDate: 'observationDate',
  periodStartDate: 'periodStartDate',
  periodEndDate: 'periodEndDate',
  applyIncidenceRateCap: 'applyIncidenceRateCap',
  incidenceRate: 'incidenceRate',
  addMonthlyAggregation: 'addMonthlyAggregation',
  applySubstandardFlatExtra: 'applySubstandardFlatExtra',
  addBuhlmannConstants: 'addBuhlmannConstants',
  applyExpectedLapseRates: 'applyExpectedLapseRates',
  includeWarnings: 'includeWarnings',
  calculateIbnr: 'calculateIbnr',
  performMonteCarlo: 'performMonteCarlo',
  treatyRestrictions: 'treatyRestrictions',
  policyRestrictions: 'policyRestrictions'
}

Parameters.inputTestIds = {
  observationDate: 'textfield-observationDate',
  periodStartDate: 'textfield-periodStartDate',
  periodEndDate: 'textfield-periodEndDate',
  applyIncidenceRateCap: 'switch-applyIncidenceRateCap',
  incidenceRate: 'textfield-incidenceRate',
  addMonthlyAggregation: 'switch-addMonthlyAggregation',
  applySubstandardFlatExtra: 'switch-applySubstandardFlatExtra',
  addBuhlmannConstants: 'switch-addBuhlmannConstants',
  applyExpectedLapseRates: 'switch-applyExpectedLapseRates',
  includeWarnings: 'switch-includeWarnings',
  calculateIbnr: 'switch-calculateIbnr',
  performMonteCarlo: 'switch-performMonteCarlo',
  treatyRestrictions: 'textfield-treatyRestrictions',
  policyRestrictions: 'textfield-policyRestrictions'
}

function renderAlert (setShowMessage) {
  return (
    <Alert
      onClose={() => setShowMessage(false)}
      severity="warning"
    >
      To proceed with IBNR calculations, please recreate common format tables
      including &#39;Termination Reported Date&#39; in column mapping.
    </Alert>
  )
}

function formatDateDisplay (date) {
  // Display as MM/DD/YYYY, value used in engine for calculation expects YYYY-MM-DD format so only display changes
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

function shouldShowAlert (showMessage, studyType, isDonColumnPresent) {
  return showMessage && !isDonColumnPresent && studyType !== 'skipMap'
}

// TODO: move the rest of this logic up to the parent component to make this a dumb component
export default function Parameters ({
  access = false,
  name = '',
  setShowMessage = () => {},
  onCalculateIbnrChange = () => {},
  observationDates = [],
  selectedObservationDate,
  setSelectedObservationDate = () => {},
  showMessage = false,
  studyType = '',
  isDonColumnPresent = false,
  'data-testid': dataTestId = ''
}) {
  const {
    control,
    register,
    formState: { errors },
    setValue,
    watch
  } = useFormContext()

  const selectMenuProps = {
    MenuProps: {
      PaperProps: {
        style: {
          maxHeight: 48 * 3.5
        }
      }
    }
  }

  const testIdProp = getTestIdProp(dataTestId);
  const showIncidentRateField = watch(Parameters.fieldNames.applyIncidenceRateCap)
  const handleApplyIncidenceRateCap = (event, value) => {
    const incidenceRate = event.target.checked ? 500 : -1
    const shouldTouch = true; //  Triggers onChange
    setValue(Parameters.fieldNames.incidenceRate, incidenceRate, { shouldTouch });
    setValue(Parameters.fieldNames.applyIncidenceRateCap, value, { shouldTouch });
  }

  const handleCalculateIbnrChange = (event) => {
    const isChecked = event.target.checked
    setShowMessage(isChecked)

    onCalculateIbnrChange(isChecked)
  }

  // the validation for observationDate is defensive code for an api call that "might" fail
  return (
    <Root display="flex" flexDirection="column" {...testIdProp}>
      <Box display="flex" justifyContent="space-between" alignContent="start">
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" width="100%">
            <Typography variant='h6'>Observation Date</Typography>
            <TextField
              select
              value={selectedObservationDate}
              inputProps={{
                'data-testid': Parameters.inputTestIds.observationDate
              }}
              {...register(Parameters.fieldNames.observationDate, { required: Parameters.errorMessages.observationDateRequired })}
              onChange={e => {
                setSelectedObservationDate(e.target.value);
                setValue(Parameters.fieldNames.observationDate, e.target.value);
              }}
              label=' '
              variant='standard'
              disabled={!access}
              error={!!errors.observationDate}
              helperText={errors.observationDate?.message}
              SelectProps={selectMenuProps}
            >
             {observationDates.map(date => (
                <MenuItem key={date} value={date}>
                  {formatDateDisplay(date)}
                </MenuItem>
             ))}
            </TextField>
          </Box>

          <Box display="flex" flexDirection="column" width="100%" paddingTop="35px">
            <Typography variant='h6'>Study Period Start</Typography>

            <TextField
              {...register(Parameters.fieldNames.periodStartDate, { required: Parameters.errorMessages.periodStartDateRequired })}
              inputProps={{
                'data-testid': Parameters.inputTestIds.periodStartDate
              }}
              label=' '
              type='date'
              variant='standard'
              disabled={!access}
              error={!!errors.periodStartDate}
              helperText={errors.periodStartDate?.message}
            />
          </Box>

          <Box display="flex" flexDirection="column" width="100%" paddingTop="35px">
            <Typography variant='h6'>Study Period End</Typography>

            <TextField
              {...register(Parameters.fieldNames.periodEndDate, { required: Parameters.errorMessages.periodEndDateRequired })}
              inputProps={{
                'data-testid': Parameters.inputTestIds.periodEndDate
              }}
              label=' '
              type='date'
              variant='standard'
              disabled={!access}
              error={!!errors.periodEndDate}
              helperText={errors.periodEndDate?.message}
            />
          </Box>
        </Box>

        <Box width="50%" display="flex" flexDirection="column" justifyContent="start" >
          <FormControl component='fieldset'>
            <FormGroup>
              <Controller
                name={Parameters.fieldNames.applyIncidenceRateCap}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      onChange={handleApplyIncidenceRateCap}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.applyIncidenceRateCap
                      }}
                    />}
                    label='Apply Per Thousand Incidence Rate Cap'
                  />
                )}
              />
              {showIncidentRateField && <div>
                <TextField
                  {...register(Parameters.fieldNames.incidenceRate)}
                  inputProps={{
                    'data-testid': Parameters.inputTestIds.incidenceRate
                  }}
                  type='number'
                  error={!!errors.incidenceRate}
                  helperText={errors.incidenceRate?.message}
                />
              </div>}
              <Controller
                name={Parameters.fieldNames.addMonthlyAggregation}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label='Apply Monthly Aggregation'
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.addMonthlyAggregation
                      }}
                      color='primary'
                    />}
                  />
                )}
              />
              <Controller
                name={Parameters.fieldNames.applySubstandardFlatExtra}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label='Apply Substandard Flat Extra'
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.applySubstandardFlatExtra
                      }}
                      color='primary'
                    />}
                  />
                )}
              />
              <Controller
                name={Parameters.fieldNames.addBuhlmannConstants}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label='Apply Buhlmann Constants'
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.addBuhlmannConstants
                      }}
                      color='primary'
                    />}
                  />
                )}
              />
              <Controller
                name={Parameters.fieldNames.applyExpectedLapseRates}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label='Apply Expected Lapse Rates'
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.applyExpectedLapseRates
                      }}
                      color='primary'
                    />}
                  />
                )}
              />
              <Controller
                name={Parameters.fieldNames.includeWarnings}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label=' Include Warnings'
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.includeWarnings
                      }}
                      color='primary'
                    />}
                  />
                )}
              />
              <Controller
                name={Parameters.fieldNames.calculateIbnr}
                control={control}
                render={({ field }) => (
                  <OuterLabelContainer>
                    <FormControlLabel
                      label='Calculate IBNR'
                      style={{ whiteSpace: 'nowrap' }}
                      control={<Switch
                        {...field}
                        checked={!!field.value}
                        onChange={(event) => {
                          field.onChange(event)
                          handleCalculateIbnrChange(event)
                        }}
                        inputProps={{
                          'data-testid': Parameters.inputTestIds.calculateIbnr
                        }}
                      />}
                    />
                    <InnerLabelContainer>
                    {shouldShowAlert(showMessage, studyType, isDonColumnPresent) && renderAlert(setShowMessage)}
                    </InnerLabelContainer>
                  </OuterLabelContainer>
                )}
              />
              <Controller
                name={Parameters.fieldNames.performMonteCarlo}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label='Perform Monte Carlo'
                    control={<Switch
                      {...field}
                      checked={!!field.value}
                      inputProps={{
                        'data-testid': Parameters.inputTestIds.performMonteCarlo
                      }}
                      color='primary'
                    />}
                  />
                )}
              />
            </FormGroup>
          </FormControl>
        </Box>
      </Box>
      <Box item xs={12}>
        <Typography variant='h6'>
          Treaty Restrictions

          <Tooltip
            title="use 'and vt.' before all column names. (e.g. and vt.source = 'GARE' and vt.treaty# = 123)"
          >
            <span><InfoOutlinedIcon /></span>
          </Tooltip>
        </Typography>

        <TextField
          {...register(Parameters.fieldNames.treatyRestrictions)}
          inputProps={{
            'data-testid': Parameters.inputTestIds.treatyRestrictions
          }}
          variant="outlined"
          placeholder="use 'and vt.' before all column names. (e.g. and vt.source = 'GARE' and vt.treaty# = 123)"
          fullWidth
          error={!!errors.treatyRestrictions}
          helperText={errors.treatyRestrictions?.message}
        />
      </Box>

      <Box paddingTop="12px">
        <Typography variant="h6">
          Policy Restrictions

          <Tooltip
            title="use 'and vp.' before all column names. (e.g. and vp.policyid = 123 and vp.issue_age between 18 and 65) "
          >
            <span><InfoOutlinedIcon /></span>
          </Tooltip>
        </Typography>

        <TextField
          {...register(Parameters.fieldNames.policyRestrictions)}
          inputProps={{
            'data-testid': Parameters.inputTestIds.policyRestrictions
          }}
          name='policyRestrictions'
          variant="outlined"
          placeholder="use 'and vp.' before all column names. (e.g. and vp.policyid = 123 and vp.issue_age between 18 and 65)"
          fullWidth
          error={!!errors.policyRestrictions}
          helperText={errors.policyRestrictions?.message}
        />
      </Box>
    </Root>
  )
}
